/* eslint-disable no-underscore-dangle */
import React, { useState, useContext } from 'react'
import { Typography, Button, Box, TextField, InputAdornment, Stack } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import { useQuery } from '@apollo/client'
import { Link as Linkreact } from 'react-router-dom'

import usersQuery from '../../graphql/query/users'
import AuthContext from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import Breadcrumb from '../../components/BreadcrumbLayout'
import { CreateUserDialog, EditUserDialog, UserTable } from '../../components/users'

const UsersPage = () => {
  const { user } = useContext(AuthContext)
  const { showSuccess } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [openCreateDialog, setOpenCreateDialog] = useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)

  let variables = {
    filter: {
      createdById: user._id,
    },
  }
  if (user.role === 'Admin') {
    variables = {}
  }
  const users = useQuery(usersQuery, { variables })

  if (user.role === 'Staff') {
    return 'Cannot Access'
  }

  // Filter users based on search term
  const filteredUsers =
    users.loading || users.data === undefined
      ? []
      : users.data.users.filter(
          (userItem) =>
            userItem.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userItem.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userItem.role?.toLowerCase().includes(searchTerm.toLowerCase())
        )

  // Pagination
  const itemsPerPage = 10
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage)
  const paginatedUsers = filteredUsers.slice((page - 1) * itemsPerPage, page * itemsPerPage)

  const handleEditClick = (userId) => {
    setSelectedUserId(userId)
    setOpenEditDialog(true)
  }

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false)
    setSelectedUserId(null)
    users.refetch()
    // Toast notification is handled in EditUserDialog component
  }

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false)
    users.refetch()
    // Toast notification is handled in CreateUserDialog component
  }

  // Determine status (for now, assume all users are active if they exist)
  const getUserStatus = (userItem) => {
    // You can add logic here based on your user model
    // For now, we'll assume all existing users are active
    return 'Active'
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        p: { xs: 2, sm: 3, md: 4 },
      }}>
      <Breadcrumb titleTypo="Users" />

      {/* Header Section */}
      <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: { xs: 0.5, sm: 0.75, md: 1 },
            color: 'text.primary',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}>
          User
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}>
          Manage user data in the system
        </Typography>

        {/* User List Section Header and Search/Add User Bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          sx={{
            mb: { xs: 2, sm: 2.5, md: 3 },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
          }}>
          {/* User List Section Header */}
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              alignSelf: { xs: 'flex-start', sm: 'center' },
            }}>
            User List
          </Typography>

          {/* Search and Add User Bar */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
            sx={{
              alignItems: { xs: 'stretch', sm: 'center' },
              flex: { xs: '1 1 100%', sm: '0 1 auto' },
              width: { xs: '100%', sm: 'auto' },
            }}>
            <TextField
              placeholder="Search user name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(1) // Reset to first page on search
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: 'text.secondary',
                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                minWidth: { xs: '100%', sm: 200, md: 250 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              sx={{
                borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 0.875, sm: 1, md: 1.125 },
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                minWidth: { xs: '100%', sm: 'auto' },
              }}>
              Add User
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Table Section */}
      <UserTable
        users={users}
        loading={users.loading}
        paginatedUsers={paginatedUsers}
        searchTerm={searchTerm}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        onEditClick={handleEditClick}
        getUserStatus={getUserStatus}
      />

      {/* Create User Dialog */}
      <CreateUserDialog open={openCreateDialog} onClose={handleCloseCreateDialog} />

      {/* Edit User Dialog */}
      {selectedUserId && (
        <EditUserDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          userId={selectedUserId}
        />
      )}
    </Box>
  )
}

export default UsersPage
